import type { CookiebotConsentCategory } from './cookiebot';

/**
 * The categories we gate work on. `necessary` is excluded on purpose: work that
 * only needs necessary cookies must never wait for consent.
 *
 * The `window.Cookiebot` shape is declared once, in `./cookiebot`.
 */
export type ConsentCategory = Exclude<CookiebotConsentCategory, 'necessary'>;

/**
 * Whether the visitor has granted a category. Deliberately fails closed: no
 * Cookiebot on the page means no consent, so nothing writes a cookie.
 */
export const hasConsent = (category: ConsentCategory): boolean =>
  typeof window !== 'undefined' &&
  !!window.Cookiebot &&
  !!window.Cookiebot.consent &&
  !!window.Cookiebot.consent[category];

/**
 * At most one pending callback per category. Route changes before an answer
 * replace it rather than stacking up, so a visitor who browses for a while and
 * then accepts gets one run, not a burst of backdated ones.
 */
const pending: Record<ConsentCategory, (() => void) | null> = {
  statistics: null,
  marketing: null,
  preferences: null,
};
let bound = false;

const flush = () => {
  (Object.keys(pending) as ConsentCategory[]).forEach(category => {
    const callback = pending[category];
    if (callback && hasConsent(category)) {
      pending[category] = null;
      callback();
    }
  });
};

const bindOnce = () => {
  if (bound || typeof window === 'undefined') return;
  bound = true;
  // Fires once per load once Cookiebot knows the state (incl. returning visitors)
  window.addEventListener('CookiebotOnConsentReady', flush);
  // Fires when the visitor accepts / updates their choice
  window.addEventListener('CookiebotOnAccept', flush);
};

/**
 * Run `cb` as soon as consent for `category` exists:
 * immediately if already granted, otherwise when the visitor grants it.
 * Safe to call on every route — listeners are bound once globally, and only
 * the latest pending callback per category is kept.
 */
export const runWithConsent = (
  category: ConsentCategory,
  cb: () => void
): void => {
  if (typeof window === 'undefined') return;
  bindOnce();
  if (hasConsent(category)) {
    cb();
  } else {
    pending[category] = cb;
  }
};
